import React from 'react';
import { Box, Typography } from '@mui/material';
import { Help } from '@mui/icons-material';
import {
  ExternalLink,
  Location,
  Phone,
  WrenchHammer,
} from '@procore/core-icons';
import {
  Flex,
  FlexList,
  colors,
  useI18nContext,
  Tooltip,
} from '@procore/core-react';
import getConfig from 'next/config';
import { getAmplitudeDeviceId } from '../Analytics/utilities';
import BusinessLogo, { LogoSize } from './BusinessLogo';
import { useStyles } from './BusinessProfileHeader.styles';
import { Business } from '../../types/business';
import StyledLink from '../ButtonsLinks/StyledLink';
import ClaimedBadge from './ClaimedBadge';
import ExpandableText from '../Layout/ExpandableText';
import DescriptionWithIcon from './DescriptionWithIcon';
import EmailObfuscationWrapper from '../utils/EmailObfuscationWrapper';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';

const { publicRuntimeConfig } = getConfig();

interface Props {
  business: Business;
  claimBusinessProfile: boolean;
  logoSize?: LogoSize;
}

const deviceId = getAmplitudeDeviceId();

const BusinessProfileHeader = ({
  business,
  claimBusinessProfile,
  logoSize = 'sm',
}: Props) => {
  const I18n = useI18nContext();
  const classes = useStyles();
  const businessDisplayAddress = business?.primaryAddress?.display
    ? `${business.primaryAddress.display.addressParts[0]}, ${business.primaryAddress.display.cityState}`
    : '';
  const businessProvidedServicesNumber =
    business?.providedServices?.length || 0;

  return (
    <Box className={classes.profileHeaderContainer}>
      <Flex className={classes.profileHeader}>
        <Box className={classes.companyLogo}>
          <BusinessLogo logoUrl={business.logoUrl} size={logoSize} />
        </Box>
        <Box className={classes.companyNameContainer}>
          <FlexList
            className={classes.mainInfoContainer}
            boxProps={{ marginLeft: 'none' }}
            alignItems="baseline"
            justifyContent="space-between"
            wrap="wrap"
            data-test-id="business-header-title-container"
          >
            <Typography variant="h1" className={classes.companyName}>
              <EmailObfuscationWrapper>
                {business.dba ? business.dba : business.name}
              </EmailObfuscationWrapper>
              {business.dba && (
                <Typography
                  className={classes.legalName}
                  data-test-id="legal-name"
                >
                  {business.name}
                  <Tooltip
                    overlay={I18n.t(
                      'pages.p.business_profile_layout.business_profile_header.legal_name'
                    )}
                    trigger="hover"
                  >
                    <Help className={classes.legalNameTooltip} />
                  </Tooltip>
                </Typography>
              )}
            </Typography>
            {claimBusinessProfile &&
              (business.claimed ? (
                <ClaimedBadge
                  text={`${I18n.t(
                    'pages.p.business_profile_layout.business_profile_header.claimed_business'
                  )}`}
                  claimedContainerStyle={classes.claimedContainer}
                />
              ) : (
                <StyledLink
                  href={`${publicRuntimeConfig.claimSignUpLink}${business.id}&device_id=${deviceId}`}
                  className={classes.headerLink}
                >
                  <Typography className={classes.isThisYourBusinessURL}>
                    {I18n.t(
                      'pages.p.business_profile_layout.business_profile_header.is_this_your_business'
                    )}
                  </Typography>
                </StyledLink>
              ))}
          </FlexList>
          <FlexList
            space="xl"
            wrap="wrap"
            className={classes.descriptiveInfoContainer}
            data-test-id="business-header-description-container"
          >
            <DescriptionWithIcon
              icon={<Location size="md" color={colors.gray40} />}
              descriptionWithIconStyle={classes.descriptionWithIcon}
              textComponent={
                <AnalyticsWrappedComponent
                  componentType={StyledLink}
                  eventText="Clicked Business Address"
                  eventLabel="Business Address"
                  data-track-click="Business Profile Header, Navigation, Business Address"
                  target="_blank"
                  href={`https://maps.google.com?q=${encodeURIComponent(
                    businessDisplayAddress
                  )}`}
                  className={classes.headerLink}
                >
                  <Typography className={classes.descriptiveInfo}>
                    <EmailObfuscationWrapper data-test-id="address">
                      {businessDisplayAddress}
                    </EmailObfuscationWrapper>
                  </Typography>
                </AnalyticsWrappedComponent>
              }
            />
            {business.phone && (
              <DescriptionWithIcon
                icon={<Phone size="md" color={colors.gray40} />}
                descriptionWithIconStyle={classes.descriptionWithIcon}
                textComponent={
                  <Typography className={classes.descriptiveInfo}>
                    {business.phone}
                  </Typography>
                }
              />
            )}
            {businessProvidedServicesNumber > 0 && (
              <DescriptionWithIcon
                icon={<WrenchHammer size="md" color={colors.gray40} />}
                descriptionWithIconStyle={classes.descriptionWithIcon}
                textComponent={
                  <Typography className={classes.descriptiveInfo}>
                    {`${businessProvidedServicesNumber} ${I18n.t(
                      'pages.p.business_profile_layout.business_profile_header.trades_and_services'
                    )}`}
                  </Typography>
                }
              />
            )}
            {business.website && (
              <DescriptionWithIcon
                icon={<ExternalLink size="md" color={colors.gray40} />}
                descriptionWithIconStyle={classes.lastDescriptionWithIcon}
                textComponent={
                  <AnalyticsWrappedComponent
                    componentType={StyledLink}
                    eventText="Clicked Business Website"
                    eventLabel="Business Website"
                    data-track-click="Business Profile Header, Navigation, Business Website"
                    data-test-id="website-link"
                    target="_blank"
                    href={business.website}
                    isExternal
                    className={classes.headerLink}
                  >
                    <Typography className={classes.descriptiveInfo}>
                      <EmailObfuscationWrapper>
                        {business.name}
                      </EmailObfuscationWrapper>
                    </Typography>
                  </AnalyticsWrappedComponent>
                }
              />
            )}
          </FlexList>
          <ExpandableText
            text={business.about}
            textClassName={classes.about}
            expandTextClassName={classes.showMoreOrLessText}
            textCutoffLength={400}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default BusinessProfileHeader;
